import { useId } from 'react';
import { formatDateTime, parseLocalInputValue, toLocalInputValue, tzAbbrev } from '@/lib/time';

export interface DateTimeValue {
  /** Raw datetime-local string, in the baby's timezone. */
  text: string;
  /** For DST-ambiguous wall times: which occurrence the user picked. */
  choice: 'earlier' | 'later';
  /** Retain sub-minute precision until the caregiver changes the input. */
  originalMs?: number;
}

export function dtFromMs(ms: number, tz: string): DateTimeValue {
  const text = toLocalInputValue(ms, tz);
  const resolved = parseLocalInputValue(text, tz);
  const choice = resolved.kind === 'ambiguous' && ms >= resolved.candidates[1] ? 'later' : 'earlier';
  return { text, choice, originalMs: ms };
}

export type ResolvedDateTime =
  | { ok: true; ms: number; ambiguous: boolean }
  | { ok: false; error: string };

export function resolveDateTime(v: DateTimeValue, tz: string): ResolvedDateTime {
  if (v.originalMs !== undefined) {
    const original = dtFromMs(v.originalMs, tz);
    if (original.text === v.text && original.choice === v.choice) {
      return { ok: true, ms: v.originalMs, ambiguous: parseLocalInputValue(v.text, tz).kind === 'ambiguous' };
    }
  }
  const r = parseLocalInputValue(v.text, tz);
  switch (r.kind) {
    case 'invalid':
      return { ok: false, error: 'Enter a valid date and time' };
    case 'nonexistent':
      return { ok: false, error: 'This time was skipped by a daylight-saving change. Choose another time.' };
    case 'ambiguous':
      return { ok: true, ms: v.choice === 'earlier' ? r.candidates[0] : r.candidates[1], ambiguous: true };
    case 'ok':
      return { ok: true, ms: r.utcMs, ambiguous: false };
  }
}

interface Props {
  label: string;
  value: DateTimeValue;
  onChange: (v: DateTimeValue) => void;
  tz: string;
  id?: string;
  onNow?: () => void;
}

/** datetime-local input that always shows which timezone the time is in, and resolves DST ambiguity explicitly. */
export function DateTimeField({ label, value, onChange, tz, id, onNow }: Props) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const res = resolveDateTime(value, tz);
  const parsed = parseLocalInputValue(value.text, tz);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label htmlFor={inputId} className="label">
          {label}
        </label>
        <span className="text-[0.7rem] text-ink-400">
          {res.ok ? tzAbbrev(res.ms, tz) : ''} · {tz}
        </span>
      </div>
      <div className="flex gap-2">
        <input
          id={inputId}
          type="datetime-local"
          className="input num"
          value={value.text}
          onChange={(e) => onChange({ text: e.target.value, choice: value.choice })}
          aria-invalid={!res.ok}
          aria-describedby={`${inputId}-help`}
          required
        />
        {onNow && (
          <button type="button" className="btn-ghost shrink-0" onClick={onNow}>
            Now
          </button>
        )}
      </div>
      <div id={`${inputId}-help`} className="min-h-4 text-xs">
        {!res.ok && <span className="text-danger">{res.error}</span>}
        {parsed.kind === 'ambiguous' && (
          <fieldset className="mt-1 rounded-xl border border-warn/30 bg-warn/10 p-2 text-ink-100">
            <legend className="px-1 text-warn">This time occurs twice (clocks went back)</legend>
            {(['earlier', 'later'] as const).map((c, i) => (
              <label key={c} className="mr-4 inline-flex min-h-9 items-center gap-2">
                <input
                  type="radio"
                  name={`${inputId}-amb`}
                  checked={value.choice === c}
                  onChange={() => onChange({ ...value, choice: c })}
                />
                {c === 'earlier' ? 'First' : 'Second'} ({tzAbbrev(parsed.candidates[i], tz)})
              </label>
            ))}
          </fieldset>
        )}
        {res.ok && parsed.kind === 'ok' && <span className="text-ink-400">{formatDateTime(res.ms, tz)}</span>}
      </div>
    </div>
  );
}
